import { useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

type Answer = 'yes' | 'no' | 'unsure' | '';

const sections = [
  {
    id: 'immediate',
    title: '1. Immediate Safety',
    color: 'purple',
    questions: [
      'Is the child currently safe from immediate harm?',
      'Does the child have a safe place to stay?',
      'Is there an immediate need for medical attention?',
    ],
  },
  {
    id: 'risk',
    title: '2. Risk Factors',
    color: 'orange',
    questions: [
      'Has the child experienced physical abuse?',
      'Has the child experienced sexual abuse?',
      'Has the child experienced emotional abuse?',
      'Is the child neglected?',
    ],
  },
  {
    id: 'child',
    title: '3. Child Information',
    color: 'blue',
    questions: [
      'Does the child have any medical conditions or disabilities?',
      'Is the child currently attending school?',
      'Does the child have a positive support system?',
    ],
  },
  {
    id: 'family',
    title: '4. Family & Living Situation',
    color: 'green',
    questions: [
      'Is the home environment safe and stable?',
      'Are basic needs being met?',
      'Are there substance abuse issues in the household?',
      'Is there domestic violence in the household?',
    ],
  },
  {
    id: 'caregiver',
    title: '5. Caregiver Capacity',
    color: 'orange',
    questions: [
      'Is the caregiver able to provide adequate care and supervision?',
      'Is the caregiver receptive to support and services?',
    ],
  },
  {
    id: 'recommendation',
    title: '6. Recommendation',
    color: 'red',
    questions: [
      'Is immediate intervention required?',
      'Should the case be referred to specialized services?',
    ],
  },
];

export default function SWAssessmentPage() {
  const { user } = useAuth();
  const [children, setChildren] = useState<any[]>([]);
  const [selectedChild, setSelectedChild] = useState<any>(null);
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [notes, setNotes] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    immediate: true,
    risk: true,
    child: true,
    family: false,
    caregiver: false,
    recommendation: false,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const submissionLock = useRef(false);

  // Configure VITE_API_BASE_URL in production, e.g.
  // https://guardiancloudcare.com/backend/api
  const API_BASE =
    (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ||
    'https://guardiancloudcare.com/backend/api';

  useEffect(() => {
    fetch(`${API_BASE}/get_assessment_children.php`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setChildren(data);
          setSelectedChild(data[0] || null);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  const totalItems = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const answered = Object.values(answers).filter(Boolean).length;
  const pending = totalItems - answered;
  const progress = Math.round((answered / totalItems) * 100);

  const riskResult = useMemo(() => {
    let score = 20;
    const flags: string[] = [];
    const protective: string[] = [];
    const recommendations: string[] = [];

    const getAnswer = (key: string) => answers[key] || '';

    if (getAnswer('immediate-0') === 'no') {
      score += 25;
      flags.push('Child may not be safe from immediate harm.');
      recommendations.push('Prioritize immediate safety planning and emergency protection.');
    } else if (getAnswer('immediate-0') === 'yes') {
      protective.push('Child is currently reported safe from immediate harm.');
    }

    if (getAnswer('immediate-1') === 'no') {
      score += 20;
      flags.push('Child may not have a safe place to stay.');
      recommendations.push('Coordinate temporary safe placement or shelter assistance.');
    } else if (getAnswer('immediate-1') === 'yes') {
      protective.push('Child has a reported safe place to stay.');
    }

    if (getAnswer('immediate-2') === 'yes') {
      score += 20;
      flags.push('Medical attention may be immediately needed.');
      recommendations.push('Refer the child for urgent medical assessment.');
    }

    if (getAnswer('risk-0') === 'yes') {
      score += 18;
      flags.push('Physical abuse indicator present.');
      recommendations.push('Document physical abuse indicators and assess safety risks.');
    }

    if (getAnswer('risk-1') === 'yes') {
      score += 25;
      flags.push('Sexual abuse indicator present.');
      recommendations.push('Refer to specialized child protection and medico-legal services.');
    }

    if (getAnswer('risk-2') === 'yes') {
      score += 12;
      flags.push('Emotional abuse indicator present.');
      recommendations.push('Provide psychosocial support and counseling referral.');
    }

    if (getAnswer('risk-3') === 'yes') {
      score += 15;
      flags.push('Neglect indicator present.');
      recommendations.push('Assess basic needs, nutrition, shelter, and caregiver capacity.');
    }

    if (getAnswer('child-0') === 'yes') {
      score += 8;
      flags.push('Medical condition or disability may increase vulnerability.');
      recommendations.push('Coordinate health or disability-related support services.');
    }

    if (getAnswer('child-1') === 'no') {
      score += 8;
      flags.push('Child may not be attending school.');
      recommendations.push('Coordinate with school or education support services.');
    } else if (getAnswer('child-1') === 'yes') {
      protective.push('Child is attending school.');
    }

    if (getAnswer('child-2') === 'no') {
      score += 10;
      flags.push('Limited positive support system.');
      recommendations.push('Identify trusted relatives, school staff, or community support.');
    } else if (getAnswer('child-2') === 'yes') {
      protective.push('Child has a positive support system.');
    }

    if (getAnswer('family-0') === 'no') {
      score += 15;
      flags.push('Home environment may not be safe or stable.');
      recommendations.push('Conduct home visit and safety assessment.');
    }

    if (getAnswer('family-1') === 'no') {
      score += 10;
      flags.push('Basic needs may not be met.');
      recommendations.push('Refer for food, shelter, medical, or financial assistance.');
    }

    if (getAnswer('family-2') === 'yes') {
      score += 10;
      flags.push('Possible substance abuse in the household.');
      recommendations.push('Assess household risks and refer caregiver to support services.');
    }

    if (getAnswer('family-3') === 'yes') {
      score += 15;
      flags.push('Possible domestic violence in the household.');
      recommendations.push('Coordinate protection planning and safety monitoring.');
    }

    if (getAnswer('caregiver-0') === 'no') {
      score += 15;
      flags.push('Caregiver may be unable to provide adequate care.');
      recommendations.push('Assess caregiver capacity and alternative care options.');
    }

    if (getAnswer('caregiver-1') === 'no') {
      score += 8;
      flags.push('Caregiver may not be receptive to services.');
      recommendations.push('Use case conference and motivational engagement strategies.');
    } else if (getAnswer('caregiver-1') === 'yes') {
      protective.push('Caregiver is receptive to support services.');
    }

    if (getAnswer('recommendation-0') === 'yes') {
      score += 15;
      flags.push('Immediate intervention is recommended.');
      recommendations.push('Prepare an urgent intervention plan and coordinate with appropriate services.');
    }

    if (getAnswer('recommendation-1') === 'yes') {
      score += 10;
      flags.push('Specialized service referral is recommended.');
      recommendations.push('Refer to specialized services based on the child’s needs.');
    }

    Object.values(answers).forEach((value) => {
      if (value === 'unsure') score += 3;
    });

    if (score > 100) score = 100;

    let level = 'LOW RISK';
    let color = 'text-green-600';
    let bg = 'bg-green-50 border-green-100';

    if (score >= 85) {
      level = 'CRITICAL RISK';
      color = 'text-red-600';
      bg = 'bg-red-50 border-red-100';
    } else if (score >= 70) {
      level = 'HIGH RISK';
      color = 'text-orange-600';
      bg = 'bg-orange-50 border-orange-100';
    } else if (score >= 45) {
      level = 'MODERATE RISK';
      color = 'text-yellow-600';
      bg = 'bg-yellow-50 border-yellow-100';
    }

    const uniqueRecommendations = [...new Set(recommendations)];

    const summary =
      flags.length > 0
        ? `The assessment identified ${flags.length} risk indicator(s). ${flags
            .slice(0, 3)
            .join(' ')} ${
            protective.length > 0
              ? `Protective factor(s) include: ${protective.slice(0, 2).join(' ')}`
              : ''
          }`
        : 'No major risk indicators were identified based on the current responses. Continue regular monitoring and documentation.';

    return {
      score,
      level,
      color,
      bg,
      flags,
      protective,
      summary,
      recommendations:
        uniqueRecommendations.length > 0
          ? uniqueRecommendations
          : ['Continue monitoring the child and update assessment when new information becomes available.'],
    };
  }, [answers]);

  const saveAssessment = async (interventionPlan: any[] = []) => {
    // A ref locks synchronously, before React has a chance to re-render.
    if (submissionLock.current || submissionSuccess) return;

    if (!selectedChild?.case_number) {
      setMessage('Please select a child first.');
      return;
    }

    submissionLock.current = true;
    setSaving(true);
    setMessage('');

    const payload = {
      case_number: selectedChild.case_number,
      uid: user?.uid,
      checklist_answers: answers,
      notes,
      risk_score: riskResult.score,
      risk_level: riskResult.level,
      ai_summary: riskResult.summary,
      recommendations: riskResult.recommendations,
      intervention_plan: interventionPlan,
      status: 'completed',
    };

    try {
      const res = await fetch(`${API_BASE}/save_child_assessment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || `Unable to save assessment (HTTP ${res.status}).`);
      }

      setMessage(data.message || 'Assessment and intervention plan saved successfully.');
      setSubmissionSuccess(true);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : 'Server error while saving assessment. Please try again.'
      );
      // Only unlock on a confirmed failure. A network failure can be ambiguous:
      // the server may have saved before the connection dropped. The backend
      // should upsert by case_number to make retrying safe.
      submissionLock.current = false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout title="Child Safety Assessment" breadcrumb={['Social Worker', 'Assessment']}>
      {(saving || submissionSuccess) && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-live="polite"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
            {saving ? (
              <>
                <div
                  className="mx-auto mb-5 h-14 w-14 animate-spin rounded-full border-4 border-teal-100 border-t-teal-600"
                  aria-label="Submitting"
                />
                <h2 className="text-xl font-bold text-gray-800">Submitting Assessment</h2>
                <p className="mt-3 text-sm text-gray-600">
                  Please wait while we save the assessment and intervention plan.
                  The duration depends on the server response. Do not close or refresh this page.
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <i className="ri-check-line text-3xl" aria-hidden="true" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">Submission Successful!</h2>
                <p className="mt-3 text-sm text-gray-600">
                  The assessment and intervention plan have been saved successfully.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSubmissionSuccess(false);
                    setMessage('');
                    setStep(1);
                    setAnswers({});
                    setNotes('');
                    setOpenSections({
                      immediate: true,
                      risk: true,
                      child: true,
                      family: false,
                      caregiver: false,
                      recommendation: false,
                    });
                  }}
                  className="mt-6 w-full rounded-xl bg-teal-600 px-5 py-3 font-semibold text-white hover:bg-teal-700"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <div className="space-y-5">
        <div className="bg-white border rounded-2xl p-5">
          <label className="text-sm font-bold text-gray-700">
            Select Child / Case Number
          </label>

          <select
            value={selectedChild?.case_number || ''}
            onChange={(e) => {
              const child = children.find((c) => c.case_number === e.target.value);
              setSelectedChild(child);
              setAnswers({});
              setNotes('');
              setStep(1);
              setMessage('');
              setSubmissionSuccess(false);
              submissionLock.current = false;
            }}
            className="mt-2 w-full border rounded-xl px-4 py-3 outline-none"
          >
            {children.map((child) => (
              <option key={child.case_number} value={child.case_number}>
                {child.case_number} — {child.first_name} {child.middle_name} {child.last_name} — {child.age} yrs — {child.gender}
              </option>
            ))}
          </select>
        </div>

        {step === 1 && (
          <ChecklistStep
            selectedChild={selectedChild}
            answers={answers}
            setAnswers={setAnswers}
            notes={notes}
            setNotes={setNotes}
            totalItems={totalItems}
            answered={answered}
            pending={pending}
            progress={progress}
            openSections={openSections}
            setOpenSections={setOpenSections}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <AIAnalysisStep
            selectedChild={selectedChild}
            answers={answers}
            riskResult={riskResult}
            onBack={() => setStep(1)}
            onNext={() => {
              setMessage('');
              setStep(3);
            }}
            saving={saving}
            message={message}
          />
        )}

        {step === 3 && (
          <InterventionPlanStep
            selectedChild={selectedChild}
            riskResult={riskResult}
            onBack={() => setStep(2)}
            onSave={saveAssessment}
            saving={saving}
            message={message}
            submissionSuccess={submissionSuccess}
          />
        )}
      </div>
    </AppLayout>
  );
}

function ChecklistStep({
  selectedChild,
  answers,
  setAnswers,
  notes,
  setNotes,
  totalItems,
  answered,
  pending,
  progress,
  openSections,
  setOpenSections,
  onNext,
}: any) {
  return (
    <div className="space-y-5">
      <Header step={1} title="Step 1 of 3: Checklist" selectedChild={selectedChild} />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat icon="ri-file-list-line" label="Total Items" value={totalItems} color="blue" />
        <Stat icon="ri-checkbox-circle-line" label="Answered" value={answered} color="green" />
        <Stat icon="ri-time-line" label="Pending" value={pending} color="orange" />

        <div className="bg-white rounded-2xl border p-4">
          <p className="text-sm text-gray-500 mb-2">Progress</p>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-right text-sm font-bold mt-1">{progress}%</p>
        </div>
      </div>

      <div className="bg-white border rounded-2xl overflow-hidden">
        {sections.map((section) => {
          const sectionAnswered = section.questions.filter((_, i) => answers[`${section.id}-${i}`]).length;
          const isOpen = openSections[section.id];

          return (
            <div key={section.id} className="border-b last:border-b-0">
              <button
                onClick={() =>
                  setOpenSections({
                    ...openSections,
                    [section.id]: !isOpen,
                  })
                }
                className={`w-full px-5 py-4 font-bold text-sm flex justify-between items-center ${sectionHeader(section.color)}`}
              >
                <span>{section.title}</span>

                <span className="flex items-center gap-3">
                  {sectionAnswered} / {section.questions.length} answered
                  <i className={isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                </span>
              </button>

              {isOpen &&
                section.questions.map((q, i) => {
                  const key = `${section.id}-${i}`;

                  return (
                    <div
                      key={key}
                      className="grid grid-cols-1 md:grid-cols-[1fr,260px,80px] gap-3 px-5 py-3 border-t items-center"
                    >
                      <p className="text-sm font-medium text-gray-700">
                        {i + 1}. {q}
                      </p>

                      <div className="flex gap-4">
                        {(['yes', 'no', 'unsure'] as Answer[]).filter(Boolean).map((a) => (
                          <label key={a} className="flex items-center gap-1 text-sm capitalize cursor-pointer">
                            <input
                              type="radio"
                              checked={answers[key] === a}
                              onChange={() => setAnswers({ ...answers, [key]: a })}
                            />
                            {a}
                          </label>
                        ))}
                      </div>

                      <button
                        onClick={() => setAnswers({ ...answers, [key]: '' })}
                        className="text-xs border rounded-lg px-3 py-2"
                      >
                        Clear
                      </button>
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border p-5">
        <label className="text-sm font-bold text-gray-700">Notes Optional</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          placeholder="Add any observations or comments about this checklist..."
          className="mt-2 w-full h-24 border rounded-xl p-3 text-sm outline-none"
        />
        <p className="text-right text-xs text-gray-400">{notes.length}/1000</p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!selectedChild}
          className="bg-teal-600 disabled:bg-teal-300 text-white px-6 py-3 rounded-xl font-semibold"
        >
          Save & Continue to AI Analysis
          <i className="ri-arrow-right-line ml-2" />
        </button>
      </div>
    </div>
  );
}

function AIAnalysisStep({
  selectedChild,
  answers,
  riskResult,
  onBack,
  onNext,
  saving,
  message,
}: any) {
  return (
    <div className="space-y-5">
      <Header step={2} title="Child Safety Assessment Checklist" selectedChild={selectedChild} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="bg-white border rounded-2xl p-5">
          <h2 className="font-bold text-gray-800 mb-4">Checklist Responses</h2>

          {sections.map((section) => (
            <div key={section.id} className="mb-4">
              <div className={`px-3 py-2 rounded-lg font-bold text-sm ${sectionHeader(section.color)}`}>
                {section.title}
              </div>

              {section.questions.map((q, i) => {
                const answer = answers[`${section.id}-${i}`] || 'unsure';

                return (
                  <div key={q} className="flex justify-between gap-3 py-2 border-b text-sm">
                    <span>{q}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${answerColor(answer)}`}>
                      {answer}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}

          <button onClick={onBack} className="border px-5 py-3 rounded-xl text-sm">
            Edit Responses
          </button>
        </div>

        <div className="space-y-5">
          <div className="bg-white border rounded-2xl p-5">
            <h2 className="font-bold text-gray-800 mb-4">
              AI Assessment Results
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Rule-Based</span>
            </h2>

            <div className={`border rounded-2xl p-5 ${riskResult.bg}`}>
              <p className="text-sm text-gray-500">Overall Risk Level</p>

              <h3 className={`text-2xl font-extrabold ${riskResult.color}`}>
                {riskResult.level}
              </h3>

              <p className="text-sm mt-2">
                Risk Score: <b>{riskResult.score}%</b>
              </p>
            </div>

            <h3 className="font-bold mt-6 mb-3">Summary</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {riskResult.summary}
            </p>

            <h3 className="font-bold mt-6 mb-3">Key Risk Indicators</h3>
            {riskResult.flags.length > 0 ? (
              riskResult.flags.map((flag: string) => (
                <Insight key={flag} icon="ri-alert-line" title="Risk Indicator" text={flag} />
              ))
            ) : (
              <p className="text-sm text-gray-400">No major risk indicators identified.</p>
            )}

            <h3 className="font-bold mt-6 mb-3">Protective Factors</h3>
            {riskResult.protective.length > 0 ? (
              riskResult.protective.map((item: string) => (
                <Insight key={item} icon="ri-shield-check-line" title="Protective Factor" text={item} />
              ))
            ) : (
              <p className="text-sm text-gray-400">No protective factors recorded yet.</p>
            )}
          </div>

          <div className="bg-white border rounded-2xl p-5">
            <h2 className="font-bold text-gray-800 mb-3">AI Recommendations</h2>
            {riskResult.recommendations.map((r: string) => (
              <div key={r} className="flex gap-3 py-2 text-sm">
                <i className="ri-checkbox-circle-line text-teal-600" />
                <span>{r}</span>
              </div>
            ))}
          </div>

          {message && (
            <div className="bg-teal-50 border border-teal-200 text-teal-700 rounded-xl p-3 text-sm">
              {message}
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={onBack} className="border px-5 py-3 rounded-xl">
              Back
            </button>

            <button
              onClick={onNext}
              className="bg-teal-600 text-white px-6 py-3 rounded-xl font-semibold"
            >
              Continue to Intervention Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InterventionPlanStep({ selectedChild, riskResult, onBack, onSave, saving, message, submissionSuccess }: any) {
  const [objectives, setObjectives] = useState([
    'Ensure the child is in a safe and stable environment.',
    'Address emotional well-being and provide psychosocial support.',
    'Strengthen family support and parenting capacity.',
    'Connect the family to community resources and services.',
  ]);

  const [notes, setNotes] = useState('');

  const interventionPlan = [
    {
      strategy: 'Ensure Safety',
      description: 'Coordinate with caregiver to ensure immediate safety and stability.',
      responsible_person: 'Social Worker',
      timeline: 'Within 2 days',
    },
    {
      strategy: 'Psychosocial Support',
      description: 'Refer the child for individual counseling and emotional support.',
      responsible_person: 'Social Worker',
      timeline: 'Within 7 days',
    },
    {
      strategy: 'Family Support',
      description: 'Conduct family sessions to strengthen caregiving and communication.',
      responsible_person: 'Social Worker',
      timeline: 'Within 14 days',
    },
  ];

  return (
    <div className="space-y-5">
      <Header step={3} title="Intervention Plan" selectedChild={selectedChild} />

      <div className="bg-white border rounded-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Info label="Overall Risk Level" value={riskResult.level} />
        <Info label="Risk Score" value={`${riskResult.score}%`} />
        <Info label="Assessment Completed" value={new Date().toLocaleString()} />
        <Info label="Case Number" value={selectedChild?.case_number || '-'} />
      </div>

      <div className="bg-white border rounded-2xl p-5">
        <h2 className="font-bold mb-3">Goals & Objectives</h2>

        <label className="text-sm font-semibold">Primary Goal</label>
        <input
          value="Ensure the child's safety, well-being, and access to appropriate support services."
          readOnly
          className="mt-2 w-full border rounded-xl p-3 text-sm"
        />

        <h3 className="font-bold mt-5 mb-3">Specific Objectives</h3>
        <div className="space-y-2">
          {objectives.map((obj, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={obj}
                onChange={(e) => {
                  const copy = [...objectives];
                  copy[i] = e.target.value;
                  setObjectives(copy);
                }}
                className="flex-1 border rounded-xl p-3 text-sm"
              />
              <button
                onClick={() => setObjectives(objectives.filter((_, index) => index !== i))}
                className="border rounded-xl px-3"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={() => setObjectives([...objectives, ''])}
          className="mt-3 border rounded-xl px-4 py-2 text-sm"
        >
          <i className="ri-add-line mr-1" />
          Add Objective
        </button>
      </div>

      <div className="bg-white border rounded-2xl p-5">
        <h2 className="font-bold mb-4">Intervention Strategies & Actions</h2>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 text-left">Strategy</th>
                <th className="p-3 text-left">Description</th>
                <th className="p-3 text-left">Responsible Person</th>
                <th className="p-3 text-left">Timeline</th>
              </tr>
            </thead>
            <tbody>
              {interventionPlan.map((row) => (
                <tr key={row.strategy} className="border-b">
                  <td className="p-3">{row.strategy}</td>
                  <td className="p-3">{row.description}</td>
                  <td className="p-3">{row.responsible_person}</td>
                  <td className="p-3">{row.timeline}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-5">
        <h2 className="font-bold mb-3">Notes Optional</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter notes..."
          className="w-full border rounded-xl p-3 h-28 text-sm"
        />
      </div>

      {message && (
        <div className="bg-teal-50 border border-teal-200 text-teal-700 rounded-xl p-3 text-sm">
          {message}
        </div>
      )}

      <div className="flex justify-between">
        <button onClick={onBack} className="border px-5 py-3 rounded-xl">
          Back to AI Results
        </button>

        <button
          onClick={() =>
            onSave([
              {
                primary_goal:
                  "Ensure the child's safety, well-being, and access to appropriate support services.",
                objectives,
                strategies: interventionPlan,
                notes,
              },
            ])
          }
          disabled={saving || submissionSuccess}
          className="bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-semibold"
        >
          {saving ? 'Submitting...' : submissionSuccess ? 'Submitted' : 'Save Intervention Plan'}
        </button>
      </div>
    </div>
  );
}

function Header({ step, title, selectedChild }: any) {
  return (
    <div className="bg-white border rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-500">
          {step === 1 && 'Review and answer all checklist items.'}
          {step === 2 && 'View analysis responses.'}
          {step === 3 && 'Create a tailored intervention plan.'}
        </p>
      </div>

      <div className="text-sm text-gray-600">
        <b>{selectedChild?.case_number || 'No Case Selected'}</b>
        {selectedChild && (
          <>
            {' '}· {selectedChild.first_name} {selectedChild.last_name} · {selectedChild.age} yrs · {selectedChild.gender}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, color }: any) {
  const colors: any = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white border rounded-2xl p-4 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
        <i className={`${icon} text-xl`} />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <h2 className="text-2xl font-bold">{value}</h2>
      </div>
    </div>
  );
}

function Info({ label, value }: any) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <h3 className="font-bold text-gray-900 mt-1">{value}</h3>
    </div>
  );
}

function Insight({ icon, title, text }: any) {
  return (
    <div className="flex gap-3 py-3">
      <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-orange-500`} />
      </div>
      <div>
        <h4 className="font-bold text-gray-800 text-sm">{title}</h4>
        <p className="text-sm text-gray-500">{text}</p>
      </div>
    </div>
  );
}

function sectionHeader(color: string) {
  const colors: any = {
    purple: 'bg-purple-50 text-purple-700',
    orange: 'bg-orange-50 text-orange-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
  };

  return colors[color];
}

function answerColor(answer: string) {
  if (answer === 'yes') return 'bg-green-100 text-green-700';
  if (answer === 'no') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
}